import {inject, Service, signal, Type} from '@angular/core';
import {AppModalDataOf} from '../../types/modal/app-modal-data.type';
import {AppDialogModal} from '../../models/modal/dialog/app-dialog-modal.model';
import {ModalService} from '../modal/modal.service';
import {AppModalVerticalPositionEnum} from '../../enums/modal/app-modal-vertical-position.enum';
import {AppModalTypeEnum} from '../../enums/modal/app-modal-type.enum';
import {AppModalHorizontalPositionEnum} from '../../enums/modal/app-modal-horizontal-position.enum';
import {TranslationKey} from '../../types/available-translations.type';
import {DialogModalComponent} from '../../components/modals/dialog-modal/dialog-modal.component';
import {ConfirmDialogModalComponent} from '../../components/dialogs/confirm-dialog-modal/confirm-dialog-modal.component';
import {AppDialogModalConfig} from '../../types/modal/dialog/app-dialog-modal-config.type';
import {AddUserToGroupStatusDialogComponent} from '../../components/dialogs/add-user-to-group-status-dialog/add-user-to-group-status-dialog.component';
import {ConnectionLostDialogComponent} from '../../components/dialogs/connection-lost-dialog/connection-lost-dialog.component';
import {AppConnectionLostDialogData} from '../../types/modal/dialog/app-connection-lost-dialog-data.type';
import {VaultMember} from '../../api/models/vault-member';
import {AppDialogMessageData} from '../../types/modal/dialog/app-dialog-message-data.type';
import {AppAddUserToGroupStatusDialogData} from '../../types/modal/dialog/app-add-user-to-group-status-dialog-data.type';
import {AppInputDialogData} from '../../models/modal/dialog/app-input-dialog-data.model';
import {InputDialogModalComponent} from '../../components/dialogs/input-dialog-modal/input-dialog-modal.component';
import {AppConfirmDialogData} from '../../models/modal/dialog/app-confirm-dialog-data.model';
import {ThemeEnum} from '../../enums/theme.enum';
import {ThemeSelectionDialogComponent} from '../../components/dialogs/theme-selection-dialog/theme-selection-dialog.component';
import {InputDialogInputOption} from '../../models/input-dialog-options.model';
import {AppDialogMessageDataInputOption} from '../../models/modal/dialog/app-dialog-message-data-input-option.model';
import {VersionNotesDialogComponent} from '../../components/dialogs/version-notes-dialog/version-notes-dialog.component';
import {SystemInfoService} from '../system-info/system-info.service';
import {ClipboardService} from '../clipboard/clipboard.service';
import {SnackbarService} from '../snackbar/snackbar.service';
import {ImportDialogResult} from '../../models/import/import-dialog-result.model';
import {ImportDialogData} from '../../models/import/import-dialog-data.model';
import {ImportDialogModalComponent} from '../../components/dialogs/import-dialog-modal/import-dialog-modal.component';
import {TagAutocompleteDialogComponent} from '../../components/dialogs/tag-autocomplete-dialog/tag-autocomplete-dialog.component';
import {TagAutocompleteMatchTable} from '../../models/tag/tag-autocomplete-match-table/tag-autocomplete-match-table.model';
import {AutocompleteSet} from '../../bfa-api/models/autocomplete-set';

@Service()
export class DialogService {
    private readonly modalService: ModalService = inject(ModalService);
    private readonly snackbarService: SnackbarService = inject(SnackbarService);
    private readonly systemInfoService: SystemInfoService = inject(SystemInfoService);
    private readonly clipboardService: ClipboardService = inject(ClipboardService);
    private readonly DIALOG_DEFAULT_ACTION_TRANSLATION_KEY: TranslationKey = 'BUTTON.OK';
    private readonly DIALOG_DEFAULT_CANCEL_TRANSLATION_KEY: TranslationKey = 'BUTTON.CANCEL';
    private currentSystemMessage?: string;
    private connectionLostDialog?: AppDialogModal<ConnectionLostDialogComponent, AppConnectionLostDialogData, boolean>;

    /**
     * Shows a plain message dialog with a single action button (defaults to "OK").
     *
     * Returns the modal itself rather than a promise — use it to close the dialog again from the outside
     * (`modalService.closeModal(dialog)`) or to react to it being closed via `dialog.afterClosed(...)`. When only the message matters,
     * the return value can be ignored.
     *
     * @param dialogData at minimum a `messageTranslationKey`; optionally a title, a custom action button label and `translateKeyParameters`
     *                   for placeholders inside those keys.
     * @param qaTag the `data-qa` value for tests; defaults to the generic dialog tag.
     */
    public showDialog(dialogData: AppDialogMessageData, qaTag?: string): AppDialogModal<DialogModalComponent, AppDialogMessageData, boolean> {
        return this.showBaseMessageDialog(
            {
                actionTranslationKey: this.DIALOG_DEFAULT_ACTION_TRANSLATION_KEY,
                ...dialogData
            },
            qaTag);
    }

    /**
     * Asks the user a yes/no question and waits for the answer.
     *
     * @param dialogData needs a `messageTranslationKey` and an `actionTranslationKey` (the confirming button); the cancel button defaults to
     *                   "Cancel".
     * @param qaTag the `data-qa` value for tests; give each call site its own so the dialogs stay distinguishable.
     * @param dialogModalConfig overrides the modal defaults, e.g. `{ shouldCloseOnBackdropClick: false, shouldCloseOnEscapeKey: false }` for a
     *                            question the user has to answer.
     * @returns `true` only if the action button was pressed. Cancelling, the backdrop click, the escape key and a programmatic close all
     *          resolve to `false` — the promise never rejects, so no `catch` is needed.
     */
    public showConfirmDialog(dialogData: AppConfirmDialogData,
                             qaTag: string = 'generic-modal-confirm-dialog',
                             dialogModalConfig: AppDialogModalConfig<ConfirmDialogModalComponent, boolean> = {}): Promise<boolean> {
        const dialogModal = this.createDialogModal<ConfirmDialogModalComponent, boolean>(
            ConfirmDialogModalComponent,
            {
                actionTranslationKey: this.DIALOG_DEFAULT_ACTION_TRANSLATION_KEY,
                cancelTranslationKey: this.DIALOG_DEFAULT_CANCEL_TRANSLATION_KEY,
                ...dialogData
            },
            qaTag,
            dialogModalConfig);

        this.modalService.addModal(dialogModal);
        return new Promise<boolean>(resolve => {
            dialogModal.afterClosed(isConfirmed => resolve(isConfirmed === true));
        });
    }

    /**
     * Ready-made confirm dialog for leaving an annotation with unsaved changes.
     *
     * @returns `true` if the user agreed to discard the changes.
     */
    public showDiscardUnsavedChangesDialog(): Promise<boolean> {
        return this.showConfirmDialog(
            {
                messageTranslationKey: 'ANNOTATION.UNSAVED_CHANGES',
                actionTranslationKey: 'BUTTON.DISCARD_CHANGES'
            },
            'discard-unsaved-changes');
    }

    /**
     * Ready-made confirm dialog for leaving the tag editing with unsaved changes.
     *
     * @returns `true` if the user agreed to discard the changes.
     */
    public showDiscardUnsavedTagChangesDialog(): Promise<boolean> {
        return this.showConfirmDialog(
            {
                messageTranslationKey: 'TAGS.UNSAVED_CHANGES',
                actionTranslationKey: 'BUTTON.DISCARD_CHANGES'
            },
            'discard-unsaved-tag-changes');
    }

    /**
     * Ready-made yes/no dialog asking whether the running stamping should be aborted.
     *
     * @returns `true` if the user chose "yes".
     */
    public showCancelStampingDialog(): Promise<boolean> {
        return this.showConfirmDialog(
            {
                messageTranslationKey: 'STAMP.STAMPING_CANCEL_CONFIRM_MSG',
                actionTranslationKey: 'BUTTON.YES',
                cancelTranslationKey: 'BUTTON.NO'
            },
            'cancel-stamping');
    }

    /**
     * Shows the progress list for adding the given users to a group. The dialog has no confirming button and reports no result — it stays
     * open until the caller closes it via `modalService.closeModal(dialog)`, which is why the modal is returned.
     *
     * @param selectedUsers the users listed in the dialog; the list is rendered once and not updated afterwards.
     */
    public showUserToGroupListStatusDialog(selectedUsers: Array<VaultMember>): AppDialogModal<
        AddUserToGroupStatusDialogComponent,
        AppAddUserToGroupStatusDialogData,
        void> {
        const dialogModal = this.createDialogModal<AddUserToGroupStatusDialogComponent, void>(
            AddUserToGroupStatusDialogComponent,
            { selectedUsers },
            'status-dialog',
            { shouldAutoFocus: false });

        this.modalService.addModal(dialogModal);
        return dialogModal;
    }

    /**
     * Dumps any value as pretty-printed JSON into a dialog, keeping its line breaks and indentation. Falls back to `String(debugData)` when
     * the value cannot be serialized, so it never throws.
     *
     * This is used for android debugging - until the android device connection bug is fixed
     * https://amagno.atlassian.net/browse/APP-1579
     *
     * @param debugData any value; objects are serialized, everything else is stringified.
     */
    public showDebugDialog(debugData: unknown): void {
        let debugMessage: string;
        try {
            debugMessage = JSON.stringify(debugData, null, 2);
        } catch (error) {
            console.error('Failed to serialize the debug data', error);
            debugMessage = String(debugData);
        }

        this.showBaseMessageDialog(
            {
                // The debug output is raw data and not a translation key, and ngx-translate echoes unknown keys unchanged.
                messageTranslationKey: debugMessage as TranslationKey,
                actionTranslationKey: this.DIALOG_DEFAULT_ACTION_TRANSLATION_KEY,
                shouldPreserveMessageFormatting: true,
                shouldUseFluidDialogWidth: true
            },
            'debug-dialog');
    }

    /**
     * Lets the user pick a theme, with `currentTheme` preselected.
     *
     * @param currentTheme the theme that is active right now; only preselects the entry, nothing is applied here.
     * @returns the chosen theme, or `undefined` if the dialog was cancelled — apply the value only when it is defined.
     */
    public showThemeSelectionDialog(currentTheme: ThemeEnum): Promise<ThemeEnum | undefined> {
        const dialogModal = this.createDialogModal<ThemeSelectionDialogComponent, ThemeEnum>(
            ThemeSelectionDialogComponent,
            { currentTheme },
            'theme-selection-dialog');

        this.modalService.addModal(dialogModal);
        return new Promise(resolve => {
            dialogModal.afterClosed(selectedTheme => resolve(selectedTheme));
        });
    }

    /**
     * Lets the user pick one of several autocomplete matches for a tag.
     *
     * @param matchTable the matches as a table; columns are the tag definitions, one row per match.
     * @returns the chosen set, or `undefined` if the dialog was cancelled — apply the values only when it is defined.
     */
    public showTagAutocompleteDialog(matchTable: TagAutocompleteMatchTable): Promise<AutocompleteSet | undefined> {
        const dialogModal = this.createDialogModal<TagAutocompleteDialogComponent, AutocompleteSet>(
            TagAutocompleteDialogComponent,
            { matchTable },
            'tag-autocomplete-dialog');

        this.modalService.addModal(dialogModal);
        return new Promise(resolve => {
            dialogModal.afterClosed(selectedSet => resolve(selectedSet));
        });
    }

    /**
     * Ready-made input dialog that makes the user type a confirmation word before a vault is deleted.
     *
     * The comparison is left to the caller: compare the returned text against `questionAnswer` and only delete on a match.
     *
     * @param vaultName shown inside the dialog text.
     * @param questionAnswer the word the user is asked to type; only rendered here, never validated.
     * @returns what the user typed, or `undefined` if the dialog was cancelled.
     */
    public async showDeleteVaultConfirmDialog(vaultName: string, questionAnswer: string): Promise<string | undefined> {
        const inputKey = 'question';
        const inputs: Map<string, InputDialogInputOption> = new Map<string, InputDialogInputOption>([
            [
                inputKey,
                {
                    placeholder: 'VAULT_DELETE_DIALOG.PLACEHOLDER',
                    value: '',
                    appTestTag: 'vault-delete-dialog-input-field'
                }
            ]
        ]);

        const inputValues = await this.showInputDialog({
            titleTranslationKey: 'VAULT_DELETE_DIALOG.TITLE',
            descriptionTranslationKey: 'VAULT_DELETE_DIALOG.QUESTION',
            saveBtnTranslationKey: 'BUTTON.DELETE',
            cancelBtnTranslationKey: 'BUTTON.CANCEL',
            translateKeyParameters: {
                vaultName,
                questionAnswer,
            },
            inputs
        }, 'delete-vault');
        return inputValues?.get(inputKey)?.value;
    }

    /**
     * Shows a server-side system message. The text is displayed as-is (it is not a translation key) and the dialog cannot be dismissed by
     * clicking the backdrop.
     *
     * Safe to call on every server response: the same message is only shown once, and an empty message just clears that memory so the next
     * occurrence is shown again.
     *
     * @param systemMessage the ready-made text from the server; `null`/`undefined`/empty shows nothing and resets the duplicate check.
     * @param forceShow shows the message again even if it is the one already shown.
     */
    public showSystemMessageDialog(systemMessage: string | null | undefined, forceShow: boolean = false): void {
        if (!systemMessage) {
            this.currentSystemMessage = undefined;
            return;
        }

        if (this.currentSystemMessage === systemMessage && !forceShow) {
            return;
        }

        this.currentSystemMessage = systemMessage;
        this.showBaseMessageDialog({
            messageTranslationKey: systemMessage as TranslationKey,
            titleTranslationKey: 'SYSTEM_MESSAGE_TITLE',
            actionTranslationKey: 'BUTTON.OK',
        }, 'system-message', {
            shouldCloseOnBackdropClick: false,
            shouldCloseOnEscapeKey: false
        });
    }

    /**
     * Tells the user the connection was lost and offers a retry. Only one such dialog may exist at a time.
     *
     * The retry runs inside the dialog: while the check is pending the dialog stays open with the retry button in a loading state,
     * on success it closes with `true`, and on failure it stays open and displays an error message within the dialog.
     * The caller only sees the final outcome.
     *
     * @param connectionCheck run inside the dialog when the user presses retry; its boolean result decides whether the dialog closes.
     * @throws if the user cancelled the dialog, or if a connection lost dialog is already open — both cases must be caught by the caller.
     */
    public async showConnectionLostDialog(connectionCheck: () => Promise<boolean>): Promise<void> {
        if (this.connectionLostDialog) {
            throw new Error('A connection lost dialog is already open.');
        }

        const dialogModal = this.createDialogModal<ConnectionLostDialogComponent, boolean>(
            ConnectionLostDialogComponent,
            { connectionCheck },
            'connection-lost',
            {
                shouldCloseOnBackdropClick: false,
                shouldCloseOnEscapeKey: false
            });
        this.connectionLostDialog = dialogModal;
        this.modalService.addModal(dialogModal);

        const isConnectionReestablished = await new Promise<boolean>(resolve => {
            dialogModal.afterClosed(reconnected => resolve(reconnected === true));
        });
        this.connectionLostDialog = undefined;

        if (!isConnectionReestablished) {
            throw new Error('The connection lost dialog was cancelled.');
        }
    }

    public closeConnectionLostDialog(): void {
        if (this.connectionLostDialog) {
            this.modalService.closeModal(this.connectionLostDialog, true);
        }
    }

    /**
     * Shows the version and system information. Copying is handled here: if the user presses the copy button, the rendered notes are written
     * to the clipboard and a success or error snackbar is shown — the caller only has to await the promise.
     */
    public async showVersionNotesDialog(): Promise<void> {
        const systemInfo = await this.systemInfoService.getSystemInfo();
        const dialogModal = this.createDialogModal<VersionNotesDialogComponent, string>(
            VersionNotesDialogComponent,
            { systemInfo },
            'version-notes-dialog');

        this.modalService.addModal(dialogModal);
        const versionNotes = await new Promise<string | undefined>(resolve => {
            dialogModal.afterClosed(renderedVersionNotes => resolve(renderedVersionNotes));
        });

        if (!versionNotes) {
            return;
        }

        try {
            await this.clipboardService.copyToClipboard(versionNotes);
            this.snackbarService.showSuccess('SHOW_VERSION_DIALOG.COPY_SUCCESS_MESSAGE');
        } catch (error) {
            this.snackbarService.showError('ERROR.GENERIC', error as Error);
        }
    }

    /**
     * Shows a dialog with one or more text fields and waits for the user to save.
     *
     * The fields are described by the `inputs` map: its keys are your own identifiers and are also the keys you read the entered values back
     * from. Each entry needs a `value` (the initial text, `''` for empty) and an `appTestTag` (`data-qa` for tests); `label`, `placeholder`
     * and `asTextarea` are optional. The map order is the render order.
     *
     * ```ts
     * const inputs = new Map<string, InputDialogInputOption>([
     *     ['name', { label: 'FOLDER.NAME', value: currentName, appTestTag: 'folder-name-input' }]
     * ]);
     * const result = await this.dialogService.showInputDialog({ titleTranslationKey: 'FOLDER.RENAME', inputs }, 'rename-folder');
     * const newName = result?.get('name')?.value;
     * ```
     *
     * @param dialogData needs `inputs`; the title, description and button labels are translation keys, and `translateKeyParameters` fills
     *                   placeholders inside them. Save/cancel default to "Save"/"Cancel".
     * @param qaTag the `data-qa` value for tests; defaults to the generic dialog tag.
     * @param dialogModalConfig per-call modal overrides, e.g. `{ shouldCloseOnBackdropClick: false }`.
     * @returns the same map keyed as `inputs`, each entry carrying the edited `value` — or `undefined` if the user cancelled. Always guard
     *          against `undefined` instead of reading the map directly.
     */
    public showInputDialog(
        dialogData: AppInputDialogData,
        qaTag?: string,
        dialogModalConfig?: AppDialogModalConfig<InputDialogModalComponent, boolean>): Promise<Map<string, AppDialogMessageDataInputOption> | undefined> {
        const dialogModal = this.createDialogModal<InputDialogModalComponent, Map<string, AppDialogMessageDataInputOption>>(
            InputDialogModalComponent,
            {
                saveBtnTranslationKey: 'BUTTON.SAVE',
                cancelBtnTranslationKey: this.DIALOG_DEFAULT_CANCEL_TRANSLATION_KEY,
                ...dialogData
            },
            qaTag,
            dialogModalConfig);
        this.modalService.addModal(dialogModal);
        return new Promise(resolve => {
            dialogModal.afterClosed(inputData => resolve(inputData));
        });
    }

    /**
     * Shows the import dialog for the given files and targets.
     *
     * @param modalData the files to import plus the preselected target; without `files` the dialog opens empty and waits for a selection.
     *
     * Returns the modal instead of a promise because the import keeps running while the dialog is open: subscribe with
     * `dialog.afterClosed(result => ...)` to receive the `ImportDialogResult`. Closing it from the outside via
     * `modalService.closeModal(dialog)` skips that result, so the listener then gets `undefined`. On a cancelled import the dialog still
     * reports a result — every file carries an error status in `statusFiles`.
     *
     * Because the import keeps running, this is the one dialog that a navigation does not close.
     */
    public showImportDialog(modalData: ImportDialogData): AppDialogModal<ImportDialogModalComponent, ImportDialogData, ImportDialogResult> {
        const dialogModal = this.createDialogModal<ImportDialogModalComponent, ImportDialogResult>(
            ImportDialogModalComponent,
            modalData,
            'import-dialog',
            {
                shouldCloseOnNavigation: false,
                shouldAutoFocus: false
            });

        this.modalService.addModal(dialogModal);
        return dialogModal;
    }

    /**
     * Creates a `DialogModalComponent` dialog and shows it immediately — the shared base for all message-style dialogs of this service.
     *
     * @param modalData the message data of the dialog component; no defaults are added here, so the callers set them themselves.
     * @param qaTag the `data-qa` value for tests; defaults to the generic dialog tag.
     * @param dialogModalConfig per-call modal overrides, e.g. `{ shouldCloseOnBackdropClick: false }`.
     */
    private showBaseMessageDialog(
        modalData: AppModalDataOf<DialogModalComponent>,
        qaTag?: string,
        dialogModalConfig?: AppDialogModalConfig<DialogModalComponent, boolean>
    ): AppDialogModal<DialogModalComponent, AppDialogMessageData, boolean> {
        const dialogModal = this.createDialogModal<DialogModalComponent, boolean>(
            DialogModalComponent,
            modalData,
            qaTag,
            dialogModalConfig);

        this.modalService.addModal(dialogModal);
        return dialogModal;
    }

    /**
     * Builds a modal, centered dialog with a backdrop for the given component, closing on a router navigation. It is not shown yet — the
     * caller has to pass it to `modalService.addModal()`.
     *
     * Two type parameters are needed: `C` is the dialog component and is inferred from `component`, `R` is the type the dialog hands back on
     * close and has to be stated explicitly — it is what the `afterClosed()` listener receives. The data type is not a type parameter: it is
     * derived from the `modalData` field of `C`, so `modalData` is type-checked against the component.
     *
     * ```ts
     * // C = ConfirmDialogModalComponent (inferred), R = boolean (stated).
     * const dialogModal = this.createDialogModal<ConfirmDialogModalComponent, boolean>(
     *     ConfirmDialogModalComponent,
     *     { messageTranslationKey: 'FOO.MESSAGE', actionTranslationKey: 'BUTTON.OK' },
     *     'my-dialog',
     *     { shouldCloseOnBackdropClick: false });
     *
     * this.modalService.addModal(dialogModal);
     * return new Promise<boolean>(resolve => {
     *     dialogModal.afterClosed(isConfirmed => resolve(isConfirmed === true));
     * });
     * ```
     *
     * Use `void` for `R` when the dialog reports nothing back, as in {@link showUserToGroupListStatusDialog}.
     *
     * @param component the dialog component to render inside the modal.
     * @param modalData its `modalData` value; the type comes from the component itself.
     * @param qaTag the `data-qa` value for tests; defaults to the generic dialog tag.
     * @param dialogModalConfig overrides the defaults, e.g. `{ shouldCloseOnBackdropClick: false }` for a dialog the user must answer.
     */
    private createDialogModal<C, R>(
        component: Type<C>,
        modalData: AppModalDataOf<C>,
        qaTag: string = 'generic-modal-dialog',
        dialogModalConfig: AppDialogModalConfig<C, R> = {}
    ): AppDialogModal<C, AppModalDataOf<C>, R> {
        return this.modalService.createModal<AppDialogModal<C, AppModalDataOf<C>, R>>({
            component,
            modalData,
            qaTag,
            isModal: true,
            hasBackdrop: true,
            shouldCloseOnNavigation: true,
            isShowing: signal(true),
            verticalPosition: AppModalVerticalPositionEnum.Middle,
            horizontalPosition: AppModalHorizontalPositionEnum.Center,
            modalType: AppModalTypeEnum.Dialog,
            ...dialogModalConfig
        });
    }
}
